import { useCallback, useMemo } from "react";
import { Task } from "@common/types/task";
import { FinancialDocument } from "@common/types/financialDocument";
import { useLinkTaskDocuments, useUnlinkTaskDocuments } from "@api/task.api";
import { useToaster } from "@design-system/ToastMessages/useToaster";

export function useTaskDocumentUpload(task: Task) {
    const { error } = useToaster();
    const linkDocuments = useLinkTaskDocuments(task);
    const unlinkDocuments = useUnlinkTaskDocuments(task);

    const attachedDocumentIds = useMemo(
        () =>
            task.financialDocumentIds ??
            task.financialDocuments?.map((doc) => doc.id) ??
            [],
        [task.financialDocumentIds, task.financialDocuments],
    );

    const handleUploadCompleted = useCallback(
        async (uploadedDocuments: FinancialDocument[]) => {
            const newIds = uploadedDocuments.map((doc) => doc.id);
            if (newIds.length === 0) {
                return;
            }
            try {
                await linkDocuments.mutateAsync(newIds);
            } catch {
                error("Failed to attach documents");
            }
        },
        [linkDocuments, error],
    );

    const attachDocument = useCallback(
        async (documentId: number) => {
            try {
                await linkDocuments.mutateAsync([documentId]);
                return true;
            } catch {
                error("Failed to attach document");
                return false;
            }
        },
        [linkDocuments, error],
    );

    const attachDocuments = useCallback(
        async (documentIds: number[]) => {
            if (documentIds.length === 0) {
                return;
            }
            try {
                await linkDocuments.mutateAsync(documentIds);
            } catch {
                error("Failed to attach documents");
            }
        },
        [linkDocuments, error],
    );

    const detachDocuments = useCallback(
        async (documentIds: number[]) => {
            if (documentIds.length === 0) {
                return true;
            }
            try {
                await unlinkDocuments.mutateAsync(documentIds);
                return true;
            } catch {
                error(
                    documentIds.length === 1
                        ? "Failed to detach document"
                        : "Failed to detach documents",
                );
                return false;
            }
        },
        [unlinkDocuments, error],
    );

    const detachDocument = useCallback(
        (documentId: number) => detachDocuments([documentId]),
        [detachDocuments],
    );

    return {
        handleUploadCompleted,
        attachDocument,
        attachDocuments,
        detachDocument,
        detachDocuments,
        attachedDocumentIds,
    };
}
